/**
 * @function getMaterialidadPermissions
 * @description Calcula y retorna todos los permisos del módulo Materialidad (facturas).
 * Esta función fábrica es invocada por `useRolePermissions` y recibe como parámetros el rol
 * del usuario y los helpers de verificación contra las listas de permisos del endpoint, de modo
 * que los permisos siempre reflejan tanto el rol como los permisos dinámicos del backend.
 *
 * Incluye permisos para:
 * - Acceso a la sección principal de facturas
 * - Listado, carga y detalle de facturas
 * - Gestión de documentos y tipos de documento en facturas
 * - Comprobantes de pago y eliminación de archivos
 *
 * @param {boolean} isUser - Indica si el usuario tiene el rol "Operativo".
 * @param {(roleCheck: boolean, sectionKey: string) => boolean} canViewByRoleOrSectionList
 *   Helper que verifica si el rol cumple la condición Y la sección está en la lista del endpoint.
 * @param {(roleCheck: boolean, actionKey: string) => boolean} canPerformByRoleOrActionList
 *   Helper que verifica si el rol cumple la condición Y la acción está en la lista del endpoint.
 * @returns Objeto plano con booleanos para cada permiso del módulo Materialidad.
 */
export const getMaterialidadPermissions = (
  isUser: boolean,
  canViewByRoleOrSectionList: (roleCheck: boolean, sectionKey: string) => boolean,
  canPerformByRoleOrActionList: (roleCheck: boolean, actionKey: string) => boolean
) => {
  return {
    // Sección principal
    canViewManagementSectionMaterialidad: isUser,
    canViewInvoices: canViewByRoleOrSectionList(isUser, 'ListadoFacturas'),
    canViewInvoicesVisor: canViewByRoleOrSectionList(isUser, 'FacturasVisor'),

    // Acciones de facturas
    canViewListInvoices: canPerformByRoleOrActionList(isUser, 'VerFacturas'),

    canLoadInvoices: canPerformByRoleOrActionList(isUser, 'CargarFactura'),
    canViewDetailInvoices: canPerformByRoleOrActionList(isUser, 'VerDetalleFactura'),
    canLoadDocumentInvoices: canPerformByRoleOrActionList(isUser, 'CargarDocumentoFactura'),
    canAddTypeDocumentInvoices: canPerformByRoleOrActionList(isUser, 'AgregarTipoDocumentoFactura'),
    canEditDocumnetInvoices: canPerformByRoleOrActionList(isUser, 'EditarDocumentoFactura'),
    // Solo depende de la actividad, sin importar el rol: quien tenga EditarDescripcion la edita.
    canEditDescriptionInvoices: canPerformByRoleOrActionList(true, 'EditarDescripcion'),
    // Igual que EditarDescripcion, pero para el detalle abierto desde el visor.
    canEditDescriptionInvoicesVisor: canPerformByRoleOrActionList(true, 'EditarDescripcionVisor'),
    canAddTicketPaymentInvoices: canPerformByRoleOrActionList(isUser, 'AgregarComprobantePagoFactura'),
    canDeleteArchiveMaterialidad: canPerformByRoleOrActionList(isUser, 'EliminarArchivo'),
    canViewListAllInvoices: canPerformByRoleOrActionList( isUser, 'VerFacturasVisor'),
    canDeleteArchiveVisor: canPerformByRoleOrActionList(isUser, 'EliminarArchivoVisor'),
    // Cambiar el tipo de documento en el detalle abierto desde el visor (EditarDocumentoFactura no aplica ahí).
    canEditDocumentoVisor: canPerformByRoleOrActionList(isUser, 'EditarDocumentoVisor'),
    canViewDetailVisor: canPerformByRoleOrActionList(isUser, 'VerDetalleFacturaVisor'),
  };
};

